/**
 * Event registrations table.
 *
 *  - Filter by status
 *  - Update attendance (status dropdown per row)
 *  - CSV export
 *  - Refund (Razorpay) for paid registrations
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Download, MoreHorizontal, Receipt } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { fetchAll, LOG_MAX_ROWS } from "@/lib/fetchAll";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { DataTable } from "@/components/data-table/DataTable";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/_app/events/$id/registrations")({ component: RegistrationsPage });

interface RegistrationRow {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  organization: string | null;
  department: string | null;
  status: "pending" | "confirmed" | "cancelled" | "attended" | "no_show";
  /** Answers to the event's custom fields, keyed by field key. */
  answers_json: string | null;
  registered_at: number;
  payment_status: string | null;
  amount_inr: number | null;
}

/** Field definitions the API returns alongside the rows. */
interface CustomField {
  key: string;
  label: string;
}

function readAnswers(json: string | null): Record<string, string> {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

function regStatusBadge(s: RegistrationRow["status"]) {
  const map: Record<RegistrationRow["status"], { label: string; variant: "default" | "secondary" | "success" | "warning" | "destructive" | "outline" }> = {
    pending:   { label: "Pending",   variant: "warning" },
    confirmed: { label: "Confirmed", variant: "success" },
    attended:  { label: "Attended",  variant: "default" },
    no_show:   { label: "No-show",   variant: "outline" },
    cancelled: { label: "Cancelled", variant: "destructive" },
  };
  const v = map[s];
  return <Badge variant={v.variant}>{v.label}</Badge>;
}

function RegistrationsPage() {
  const { id } = Route.useParams();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [refundOpenFor, setRefundOpenFor] = useState<RegistrationRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["events", id, "registrations"],
    queryFn: () => api.get<{ items: RegistrationRow[]; fields: CustomField[] }>(`/admin/events/${id}/registrations`),
  });

  const setStatus = useMutation({
    mutationFn: ({ regId, status }: { regId: number; status: RegistrationRow["status"] }) =>
      api.patch(`/admin/events/${id}/registrations/${regId}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events", id, "registrations"] }),
  });

  const columns: ColumnDef<RegistrationRow, unknown>[] = [
    { accessorKey: "name", header: "Name", cell: ({ row }) => <div>
        <p className="font-medium">{row.original.name}</p>
        <p className="text-xs text-muted-foreground">{row.original.email}</p>
      </div>
    },
    { accessorKey: "phone", header: "Phone" },
    { accessorKey: "department", header: "Dept" },
    // One column per custom question the event defines, in the admin's order.
    ...(data?.fields ?? []).map((f): ColumnDef<RegistrationRow, unknown> => ({
      id: `answer_${f.key}`,
      header: f.label,
      cell: ({ row }) => {
        const v = readAnswers(row.original.answers_json)[f.key];
        return v ? <span className="text-sm">{v}</span> : <span className="text-xs text-muted-foreground">—</span>;
      },
    })),
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => regStatusBadge(row.original.status),
      filterFn: (r, _i, v) => r.getValue("status") === v,
    },
    {
      id: "payment",
      header: "Payment",
      cell: ({ row }) => row.original.amount_inr == null
        ? <span className="text-xs text-muted-foreground">Free</span>
        : (
          <div className="text-sm">
            <p className="font-mono">₹{(row.original.amount_inr / 100).toLocaleString("en-IN")}</p>
            <p className="text-xs text-muted-foreground">{row.original.payment_status ?? "—"}</p>
          </div>
        ),
    },
    {
      accessorKey: "registered_at", header: "Registered",
      cell: ({ row }) => format(row.original.registered_at * 1000, "MMM d, h:mma"),
    },
    {
      id: "actions", header: "",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {(["pending", "confirmed", "attended", "no_show", "cancelled"] as const).map((s) => (
              <DropdownMenuItem
                key={s}
                disabled={!can("event_regs", "update") || row.original.status === s}
                onClick={() => setStatus.mutate({ regId: row.original.id, status: s })}
              >
                Mark as {s.replace("_", "-")}
              </DropdownMenuItem>
            ))}
            {row.original.payment_status === "captured" && (
              <DropdownMenuItem
                disabled={!can("payments", "refund")}
                onClick={() => setRefundOpenFor(row.original)}
              >
                <Receipt className="h-3.5 w-3.5" /> Refund payment
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      <PageHeader
        title="Registrations"
        back={{ href: `/events/${id}`, label: "Event" }}
        actions={
          can("event_regs", "export") && (
            <Button variant="outline" size="sm" asChild>
              <a href={`${apiBase}/admin/events/${id}/registrations/export`} download>
                <Download className="h-4 w-4" /> CSV
              </a>
            </Button>
          )
        }
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        globalSearch
        globalSearchPlaceholder="Search by name, email, phone…"
        filters={[{ id: "status", label: "Status", options: [
          { value: "pending", label: "Pending" }, { value: "confirmed", label: "Confirmed" },
          { value: "attended", label: "Attended" }, { value: "no_show", label: "No-show" },
          { value: "cancelled", label: "Cancelled" },
        ]}]}
        emptyMessage={isLoading ? "Loading…" : "No registrations yet."}
      />

      {refundOpenFor && (
        <RefundDialog
          registration={refundOpenFor}
          eventId={parseInt(id, 10)}
          onClose={() => setRefundOpenFor(null)}
        />
      )}

      <p className="pt-4 text-xs text-muted-foreground">
        Public registration form: <Link to={`/events/${id}` as never} className="text-primary">/events/&lt;slug&gt;</Link>
      </p>
    </div>
  );
}

const refundSchema = z.object({
  amount_rupees: z.coerce.number().positive(),
  reason: z.string().max(500).optional(),
});
type RefundFormValues = z.infer<typeof refundSchema>;

function RefundDialog({
  registration, eventId, onClose,
}: {
  registration: RegistrationRow;
  eventId: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { register, handleSubmit, formState: { errors } } = useForm<RefundFormValues>({
    resolver: zodResolver(refundSchema),
    defaultValues: { amount_rupees: (registration.amount_inr ?? 0) / 100, reason: "" },
  });

  // First we need the underlying payment id. The API `/admin/events/:id/registrations`
  // returns payment_status + amount_inr but not the payment row id. We fetch it
  // by listing payments filtered to this registration via /admin/payments.
  const { data: paymentRow, isLoading } = useQuery({
    queryKey: ["payment", "by-reg", registration.id],
    queryFn: async () => {
      // Scan-by-fetch: /admin/payments has no server-side related_id filter, so
      // the match happens client-side and can only find what was loaded. The
      // window is bounded (LOG_MAX_ROWS) because the ledger grows without limit
      // — so once payments outgrow it, this lookup starts missing older rows and
      // the refund button goes dead with a "No payment found" error. The real
      // fix is a `related_resource`/`related_id` filter on the API route.
      const r = await fetchAll<{ id: number; related_resource: string; related_id: number; status: string }>(
        "/admin/payments", { maxRows: LOG_MAX_ROWS },
      );
      return r.items.find((p) => p.related_resource === "event_registration" && p.related_id === registration.id);
    },
  });

  const refund = useMutation({
    mutationFn: (v: RefundFormValues) => {
      if (!paymentRow) throw new Error("No payment found for this registration.");
      return api.post(`/admin/payments/${paymentRow.id}/refund`, {
        amount_inr: Math.round(v.amount_rupees * 100),
        reason: v.reason,
      });
    },
    onSuccess: () => {
      toast.success("Refund issued");
      qc.invalidateQueries({ queryKey: ["events", String(eventId), "registrations"] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Refund failed"),
  });

  return (
    <Dialog open onOpenChange={(b) => !b && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Refund payment</DialogTitle>
          <DialogDescription>
            Razorpay will refund to the original payment method. The refund typically settles within 5–7 business days.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit((v) => refund.mutate(v))}>
          <div className="rounded-md border bg-secondary/30 p-3 text-sm">
            <p><span className="text-muted-foreground">Registrant:</span> <strong>{registration.name}</strong> ({registration.email})</p>
            <p><span className="text-muted-foreground">Original amount:</span> ₹{((registration.amount_inr ?? 0) / 100).toLocaleString("en-IN")}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="amount_rupees">Refund amount (₹)</Label>
            <Input id="amount_rupees" type="number" step="0.01" min={0.01} {...register("amount_rupees")} />
            {errors.amount_rupees && <p className="text-xs text-destructive">{errors.amount_rupees.message}</p>}
            <p className="text-xs text-muted-foreground">For a partial refund, enter a smaller value.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Input id="reason" placeholder="Event cancelled, etc." {...register("reason")} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={refund.isPending || isLoading || !paymentRow}>
              {refund.isPending ? "Refunding…" : "Issue refund"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
